import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Users, Search } from 'lucide-react';
import { useSelector } from 'react-redux';
import { selectCompanyName } from '@/store/slices/companySlice';
import { useEmployees, useDepartments, useDesignations } from '@/api/queries/employee.queries';
import { StatCard } from '@/components/shared/StatCard';
import { ReportExportBar } from '@/components/reports/ReportExportBar';
import { buildActiveEmployeeRows } from '@/lib/ReportGenerator';

// Deterministic mock leave/attendance per employee
function mockStats(emp) {
  const seed = emp.id?.charCodeAt(emp.id.length - 1) || 1;
  const presentDays = 12 + (seed % 8);           // 12–19
  const totalDays   = 22;
  const leaveEl     = Math.floor(seed / 4) % 4;  // 0–3
  const leaveCl     = (seed % 3);                // 0–2
  const leaveSl     = (seed % 2);                // 0–1
  const pct         = Math.round((presentDays / totalDays) * 100);
  return { presentDays, totalDays, leaveEl, leaveCl, leaveSl, pct };
}

const PCT_BADGE = (pct) => {
  if (pct >= 90) return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
  if (pct >= 75) return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
  return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
};

const EMP_TYPE_COLOR = {
  FULL_TIME:   'bg-indigo-50 dark:bg-indigo-900/20',
  PART_TIME:   'bg-sky-50 dark:bg-sky-900/20',
  CONTRACT:    'bg-orange-50 dark:bg-orange-900/20',
  DAILY_WAGE:  '',
};

export default function ActiveEmployeesReportPage() {
  const companyName = useSelector(selectCompanyName);
  const [search, setSearch]     = useState('');
  const [deptFilter, setDept]   = useState('');
  const [desgFilter, setDesg]   = useState('');
  const [typeFilter, setType]   = useState('');

  const { data: empData, isLoading } = useEmployees({ status: 'ACTIVE', limit: 500 });
  const { data: deptData }           = useDepartments();
  const { data: desgData }           = useDesignations();

  const departments   = deptData?.data || [];
  const designations  = desgData?.data || [];
  const allEmployees  = empData?.data  || [];

  const filtered = useMemo(() => {
    let list = allEmployees.filter(e => e.status === 'ACTIVE' || !e.status);
    if (search)     list = list.filter(e => `${e.first_name} ${e.last_name} ${e.employee_code}`.toLowerCase().includes(search.toLowerCase()));
    if (deptFilter) list = list.filter(e => (e.department?.id || e.department_id) === deptFilter);
    if (desgFilter) list = list.filter(e => (e.designation?.id || e.designation_id) === desgFilter);
    if (typeFilter) list = list.filter(e => e.employment_type === typeFilter);
    return list;
  }, [allEmployees, search, deptFilter, desgFilter, typeFilter]);

  // KPI counts
  const kpi = useMemo(() => ({
    total:     allEmployees.length,
    fullTime:  allEmployees.filter(e => e.employment_type === 'FULL_TIME').length,
    partTime:  allEmployees.filter(e => e.employment_type === 'PART_TIME').length,
    contract:  allEmployees.filter(e => e.employment_type === 'CONTRACT').length,
    dailyWage: allEmployees.filter(e => e.employment_type === 'DAILY_WAGE').length,
  }), [allEmployees]);

  const exportRows = useMemo(() => buildActiveEmployeeRows(
    filtered.map(e => ({ ...e, ...mockStats(e) }))
  ), [filtered]);

  const filterSummary = [
    departments.find(d => d.id === deptFilter)?.name,
    designations.find(d => d.id === desgFilter)?.name,
    typeFilter,
  ].filter(Boolean).join(', ') || 'All active employees';

  return (
    <div className="p-6 space-y-5">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-[12px] text-[var(--text-tertiary)]">
        <Link to="/reports" className="hover:text-[var(--accent)]">Reports</Link>
        <ChevronRight size={12}/>
        <span className="text-[var(--text-primary)] font-medium">Active Employees</span>
      </nav>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-[var(--text-primary)]">Active Employee Report</h1>
          <p className="text-[13px] text-[var(--text-secondary)] mt-0.5">June 2026 · {companyName}</p>
        </div>
        <ReportExportBar rows={exportRows} filename="active-employees" title="Active Employee Report" companyName={companyName} filterSummary={filterSummary}/>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard title="Total Active"  value={kpi.total}     icon={Users} color="blue"/>
        <StatCard title="Full-Time"     value={kpi.fullTime}  icon={Users} color="green"/>
        <StatCard title="Part-Time"     value={kpi.partTime}  icon={Users} color="yellow"/>
        <StatCard title="Contract"      value={kpi.contract}  icon={Users} color="purple"/>
        <StatCard title="Daily Wage"    value={kpi.dailyWage} icon={Users} color="red"/>
      </div>

      {/* Filters */}
      <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-4 flex flex-wrap gap-3">
        <div className="flex items-center gap-1.5 flex-1 min-w-[180px]">
          <Search size={14} className="text-[var(--text-tertiary)] shrink-0"/>
          <input
            placeholder="Search name, code…"
            value={search} onChange={e => setSearch(e.target.value)}
            className="flex-1 text-[13px] bg-transparent outline-none placeholder:text-[var(--text-tertiary)]"
          />
        </div>
        <select value={deptFilter} onChange={e => setDept(e.target.value)}
          className="h-8 px-2.5 text-[12px] bg-[var(--bg-app)] border border-[var(--border)] rounded-lg outline-none focus:border-[var(--accent)]">
          <option value="">All Departments</option>
          {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <select value={desgFilter} onChange={e => setDesg(e.target.value)}
          className="h-8 px-2.5 text-[12px] bg-[var(--bg-app)] border border-[var(--border)] rounded-lg outline-none focus:border-[var(--accent)]">
          <option value="">All Designations</option>
          {designations.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <select value={typeFilter} onChange={e => setType(e.target.value)}
          className="h-8 px-2.5 text-[12px] bg-[var(--bg-app)] border border-[var(--border)] rounded-lg outline-none focus:border-[var(--accent)]">
          <option value="">All Types</option>
          <option value="FULL_TIME">Full-Time</option>
          <option value="PART_TIME">Part-Time</option>
          <option value="CONTRACT">Contract</option>
          <option value="DAILY_WAGE">Daily Wage</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-[var(--border)] bg-[var(--bg-app)]">
                {['#', 'Code', 'Name', 'Department', 'Designation', 'Type', 'Join Date', 'Present', 'Leave (EL/CL/SL)', 'Attendance %'].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-[11px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i} className="border-b border-[var(--border)]">
                    {Array.from({ length: 10 }).map((_, j) => (
                      <td key={j} className="px-4 py-3"><div className="h-4 bg-[var(--bg-hover)] rounded animate-pulse w-20"/></td>
                    ))}
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-10 text-center text-[var(--text-tertiary)]">No employees match the selected filters</td>
                </tr>
              ) : filtered.map((emp, idx) => {
                const stats = mockStats(emp);
                const typeColor = EMP_TYPE_COLOR[emp.employment_type] || '';
                return (
                  <tr key={emp.id} className={`border-b border-[var(--border)] last:border-0 hover:bg-[var(--bg-hover)] transition-colors ${typeColor}`}>
                    <td className="px-4 py-3 text-[var(--text-tertiary)]">{idx + 1}</td>
                    <td className="px-4 py-3 font-mono text-[12px] text-[var(--text-tertiary)]">{emp.employee_code}</td>
                    <td className="px-4 py-3 font-medium text-[var(--text-primary)] whitespace-nowrap">
                      {emp.first_name} {emp.last_name}
                    </td>
                    <td className="px-4 py-3 text-[var(--text-secondary)]">{emp.department?.name || '—'}</td>
                    <td className="px-4 py-3 text-[var(--text-secondary)]">{emp.designation?.name || emp.designation || '—'}</td>
                    <td className="px-4 py-3">
                      <span className="text-[11px] font-medium text-[var(--text-secondary)]">
                        {emp.employment_type?.replace('_', ' ') || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[var(--text-secondary)] whitespace-nowrap">
                      {emp.date_of_joining ? new Date(emp.date_of_joining).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                    </td>
                    <td className="px-4 py-3 text-[var(--text-primary)] font-medium">
                      {stats.presentDays} <span className="text-[var(--text-tertiary)] font-normal">/ {stats.totalDays}</span>
                    </td>
                    <td className="px-4 py-3 text-[var(--text-secondary)]">
                      <span className="inline-flex gap-1 text-[12px]">
                        <span className="text-blue-600">EL:{stats.leaveEl}</span>
                        <span className="text-amber-600">CL:{stats.leaveCl}</span>
                        <span className="text-red-500">SL:{stats.leaveSl}</span>
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold ${PCT_BADGE(stats.pct)}`}>
                        {stats.pct}%
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-[var(--border)] bg-[var(--bg-app)] text-[12px] text-[var(--text-tertiary)]">
          Showing {filtered.length} of {allEmployees.length} active employees
        </div>
      </div>
    </div>
  );
}
